// src/pages/account/Settings.js - Account settings and preferences
import React, { useState } from 'react';
import styled from 'styled-components';

const SettingsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  cursor: pointer;
  flex: 1;
`;

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const Button = styled.button`
  padding: 12px 20px;
  background: ${props => props.danger ? '#dc3545' : '#007AFF'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.danger ? '#c82333' : '#0056b3'};
  }
`;

const Settings = ({ user }) => {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [privateGallery, setPrivateGallery] = useState(true);
  
  return (
    <SettingsContainer>
      <h1>Settings</h1>
      <p>Manage your account preferences and notification settings.</p>
      
      <Section>
        <SectionTitle>📱 Communication Consent Status</SectionTitle>
        
        <div style={{ 
          background: '#e7f3ff', 
          border: '1px solid #b3d9ff', 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '20px' 
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#333' }}>
            ✅ Your Communication Consent is Active
          </div>
          <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.4' }}>
            By using ShowingBooker, you have consented to receive SMS and email notifications 
            essential to the service. This includes booking confirmations, reminders, and coordination messages.
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
            <strong>Opt-out options:</strong> Reply STOP to SMS messages or click unsubscribe in emails. 
            Continued opt-out may limit platform functionality.
          </div>
        </div>
        
        <SettingItem>
          <CheckboxContainer>
            <StyledCheckbox
              type="checkbox" 
              id="email-notifications" 
              checked={emailNotifications} 
              onChange={() => setEmailNotifications(!emailNotifications)} 
            />
            <div>
              <Label htmlFor="email-notifications">
                Optional marketing emails
              </Label>
              <Description>
                Receive updates about platform features and promotional announcements (optional)
              </Description>
            </div>
          </CheckboxContainer>
        </SettingItem>
        
        <div style={{ 
          background: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '8px', 
          padding: '16px', 
          marginTop: '20px' 
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#856404' }}>
            ⚠️ Don't Want SMS/Email Notifications?
          </div>
          <div style={{ fontSize: '14px', color: '#856404', marginBottom: '12px' }}>
            Essential SMS and email communications cannot be disabled as they are required for 
            ShowingBooker services to function properly.
          </div>
          <div style={{ fontSize: '13px', color: '#856404' }}>
            If you do not agree to receive these communications, you must delete your account. 
            See our <a href="/showingbooker/terms" style={{ color: '#856404', textDecoration: 'underline' }}>Terms of Service</a> for details.
          </div>
        </div>
      </Section>
      
      <Section>
        <SectionTitle>🔒 Privacy Settings</SectionTitle>
        
        <SettingItem>
          <CheckboxContainer>
            <StyledCheckbox
              type="checkbox" 
              id="private-gallery" 
              checked={privateGallery} 
              onChange={() => setPrivateGallery(!privateGallery)} 
            />
            <div>
              <Label htmlFor="private-gallery">
                Make new images private by default
              </Label>
              <Description>
                New generated images will be private and only visible to you
              </Description>
            </div>
          </CheckboxContainer>
        </SettingItem>
      </Section>
      
      <Section>
        <SectionTitle>⚠️ Account Management</SectionTitle>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div>
            <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>Account Email</h4>
            <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
              {user?.email}
            </p>
            <Description>
              To change your email address, please contact support
            </Description>
          </div>
          
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
            <Button danger onClick={() => alert('Account deletion feature coming soon. Contact support if needed.')}>
              🗑️ Delete Account
            </Button>
            <Description style={{ marginTop: '8px' }}>
              Permanently delete your account and all associated data. This action cannot be undone.
            </Description>
          </div>
        </div>
      </Section>
    </SettingsContainer>
  );
};

export default Settings;